import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { CampaignManagementService } from '../../../services/campaignManagementService';

const CampaignEditModal = ({ 
  isOpen, 
  onClose, 
  campaign, 
  onCampaignUpdated 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'draft',
    startDate: '',
    endDate: '',
    targetScans: 0
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when campaign changes
  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign?.name || '',
        description: campaign?.description || '',
        status: campaign?.status || 'draft',
        startDate: campaign?.start_date ? campaign?.start_date?.split('T')?.[0] : '',
        endDate: campaign?.end_date ? campaign?.end_date?.split('T')?.[0] : '',
        targetScans: campaign?.target_scans || 0
      });
      setError('');
    }
  }, [campaign]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(''); // Clear error when user makes changes
  };

  const validateForm = () => {
    if (!formData?.name?.trim()) {
      setError('Campaign name is required');
      return false;
    }

    if (formData?.startDate && formData?.endDate) {
      if (new Date(formData?.startDate) >= new Date(formData?.endDate)) {
        setError('End date must be after start date');
        return false;
      }
    }

    if (formData?.targetScans < 0) {
      setError('Target scans must be a positive number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      const updates = {
        name: formData?.name?.trim(),
        description: formData?.description?.trim() || null,
        status: formData?.status,
        start_date: formData?.startDate ? new Date(formData?.startDate)?.toISOString() : null,
        end_date: formData?.endDate ? new Date(formData?.endDate)?.toISOString() : null,
        target_scans: parseInt(formData?.targetScans) || 0
      };

      const { data, error } = await CampaignManagementService?.updateCampaign(campaign?.id, updates);

      if (error) {
        setError(error);
        return;
      }

      // Call parent callback with updated campaign
      onCampaignUpdated?.(data);
      onClose();
    } catch (error) {
      setError(`Failed to update campaign: ${error?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusToggle = async (newStatus) => {
    setIsLoading(true);
    setError('');

    try {
      const updates = { status: newStatus };
      const { data, error } = await CampaignManagementService?.updateCampaign(campaign?.id, updates);

      if (error) {
        setError(error);
        return;
      }

      // Update local form data
      setFormData(prev => ({ ...prev, status: newStatus }));
      
      // Call parent callback
      onCampaignUpdated?.(data);
    } catch (error) {
      setError(`Failed to ${newStatus === 'active' ? 'activate' : 'deactivate'} campaign: ${error?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-700 bg-green-100';
      case 'paused': return 'text-yellow-700 bg-yellow-100';
      case 'completed': return 'text-blue-700 bg-blue-100';
      case 'archived': return 'text-gray-700 bg-gray-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'paused': return 'Pause';
      case 'completed': return 'CheckCircle';
      case 'archived': return 'Archive';
      case 'draft': return 'FileText';
      default: return 'Circle';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Campaign</h2>
            <p className="text-sm text-gray-500 mt-1">
              Update campaign details and manage status
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="w-8 h-8"
            disabled={isLoading}
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 mr-2" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Campaign Status Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Campaign Status</h3>
              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(formData?.status)}`}>
                <Icon name={getStatusIcon(formData?.status)} size={12} />
                {formData?.status?.charAt(0)?.toUpperCase() + formData?.status?.slice(1)}
              </div>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              {formData?.status !== 'active' && (
                <Button
                  type="button"
                  onClick={() => handleStatusToggle('active')}
                  disabled={isLoading}
                  size="sm"
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  <Icon name="Play" size={14} className="mr-1" />
                  Activate
                </Button>
              )}
              
              {formData?.status === 'active' && (
                <Button
                  type="button"
                  onClick={() => handleStatusToggle('paused')}
                  disabled={isLoading}
                  size="sm"
                  className="bg-yellow-600 text-white hover:bg-yellow-700"
                >
                  <Icon name="Pause" size={14} className="mr-1" />
                  Pause
                </Button>
              )}

              {formData?.status !== 'archived' && (
                <Button
                  type="button"
                  onClick={() => handleStatusToggle('archived')}
                  disabled={isLoading}
                  size="sm"
                  variant="outline"
                  className="border-gray-300"
                >
                  <Icon name="Archive" size={14} className="mr-1" />
                  Archive
                </Button>
              )}

              {(formData?.status === 'paused' || formData?.status === 'archived') && (
                <Button
                  type="button"
                  onClick={() => handleStatusToggle('active')}
                  disabled={isLoading}
                  size="sm"
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  <Icon name="Play" size={14} className="mr-1" />
                  Reactivate
                </Button>
              )}
            </div>
          </div>

          {/* Campaign Details Form */}
          <div className="space-y-4">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Name *
              </label>
              <Input
                type="text"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                placeholder="Enter campaign name"
                disabled={isLoading}
                className="w-full"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder="Campaign description (optional)"
                disabled={isLoading}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={formData?.startDate}
                  onChange={(e) => handleChange('startDate', e?.target?.value)}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={formData?.endDate}
                  onChange={(e) => handleChange('endDate', e?.target?.value)}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
            </div>

            {/* Target Scans */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Scans
              </label>
              <Input
                type="number"
                min="0"
                value={formData?.targetScans}
                onChange={(e) => handleChange('targetScans', e?.target?.value)}
                placeholder="0"
                disabled={isLoading}
                className="w-full"
              />
            </div>

            {/* Status Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Status
              </label>
              <select
                value={formData?.status}
                onChange={(e) => handleChange('status', e?.target?.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  Update Campaign
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignEditModal;